import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel } from "swiper/modules";
import "swiper/css";
import axiosInstance from "../api/axiosInstance";
import { isVideoFile } from '../utils/mediaUtils';
import PlaceDetails from "./TrippinPost/components/PlaceDetails";
import StickyPlaceIcons from "./TrippinPost/components/StickyPlaceIcons";
import '../styles/trippin.css';
import { CircularProgress } from "@mui/material";

const PlaceView = () => {
  const { placeId } = useParams();
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPlaceDetails = async () => {
    setLoading(true);
    try {
      const resp = await axiosInstance.get(`/saved/places/${placeId}`);
      const fetchedPlace = resp.data.place;
      setPlace(fetchedPlace);
    } catch (error) {
      console.error("Error fetching place details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlaceDetails();
  }, [placeId]);

  if (loading) {
    return (
      <div className="loading-container">
        <CircularProgress size={40} sx={{ color: '#d1d5db' }} />
      </div>
    );
  }

  if (!place) {
    return (
      <div className="loading-container" style={{ height: "unset" }}>
        <p style={{ fontSize: "18px", color: "#d1d5db", textAlign: "center", marginTop: "50px" }}>
          No saved place found        
        </p>
      </div>
    );
  }

  return (
    <div style={{ width: "100vw", height: "100vh" }}>
      <SinglePlaceView place={place} />
    </div>
  );
};

export default PlaceView;

const SinglePlaceView = ({ place }) => {
  const verticalSwiperRef = useRef(null);

  const stickyPlace = useMemo(() => ({
    id: place?.places?.[0]?.place_id || place?.id,
    post_place_ids: place?.places?.[0]?.post_place_id ? [place.places[0].post_place_id] : [],
    is_saved: true,
    latitude: place?.latitude || place?.places?.[0]?.latitude,
    longitude: place?.longitude || place?.places?.[0]?.longitude,
    place_name: place?.place_name || place?.places?.[0]?.place_name,
  }), [place]);

  const detailsPlace = useMemo(() => ({
    place_name: place?.place_name || place?.places?.[0]?.place_name,
    country: place?.places?.[0]?.country || place?.country,
    caption: place?.places?.[0]?.caption,
    review: place?.places?.[0]?.review,
    price: place?.places?.[0]?.price,
    currency: place?.places?.[0]?.currency || place?.currency,
    companionship: place?.places?.[0]?.companionship,
  }), [place]);

  const images = useMemo(() => {
    let list = Array.isArray(place.photos) && place.photos.length > 0
      ? place.photos.map(photo => photo.content_url).filter(Boolean)
      : (Array.isArray(place.places)
          ? place.places.map(p => p.content_url).filter(Boolean)
          : []);
    if (!list || list.length === 0) {
      list = ["/assets/images/defaultPlaceholder.jpg"];
    }
    return list;
  }, [place]);

  return (
    <Swiper
      ref={verticalSwiperRef}
      direction="vertical"
      modules={[Mousewheel]}
      mousewheel={false}
      nested
      style={{ width: "100%", height: "100%" }}
    >
      <SwiperSlide>
  <div className="flex flex-col w-full h-full bg-black">
          <div className="z-10 flex items-center justify-between p-3 mt-8">
            <div className="flex items-center gap-2">
              <img
                src={place?.places?.[0]?.profile_picture || "/assets/images/default_profile.png"}
                alt="User Profile"
                className="h-9 w-9 rounded-full object-cover shadow"
              />
              <div className="text-white text-base font-medium drop-shadow">
                {place?.places?.[0]?.username || 'User'}
              </div>
            </div>
          </div>

          <div className="relative w-full aspect-[9/16] max-h-[70vh] mx-auto">
            <Swiper direction="horizontal" modules={[Mousewheel]} mousewheel={false} nested>
              {images.map((imgUrl, index) => {
                const isVideo = isVideoFile(imgUrl);
                return (
                  <SwiperSlide key={index}>
                    {isVideo ? (
                      <video src={imgUrl} className="h-full w-full object-cover" autoPlay loop playsInline />
                    ) : (
                      <img src={imgUrl} alt={`PlaceImage-${index}`} className="h-full w-full object-cover" />
                    )}
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/60 pointer-events-none rounded-b" />
            <StickyPlaceIcons trippinId={place?.trippin_post_id} place={stickyPlace} source="saved" />
          </div>

          {/* Place details now below the media container */}
          <div className="w-full pt-4">
            <PlaceDetails place={detailsPlace} />
          </div>
        </div>
      </SwiperSlide>
    </Swiper>
  );
};